/**
 * Working out which part of the body meets the ball.
 *
 * The clip files never say. The engine works it out at load: it poses a spare skeleton at
 * the contact frame, measures every body part against the authored ball position, and keeps
 * the name of the nearest one (`AddExtraTouches`, animcollection.cpp:882-910). All the game
 * ever asks of that name is whether it mentions a foot or a lower leg — that is what
 * separates a struck kick from a chest or thigh nudge, and a goalkeeper may not pick up a
 * kick from his own side.
 *
 * The original does this offline in the same pass that builds the library. Here it happens
 * once at start-up instead of in the asset converter, because the converter is a Python
 * script with no skeleton and the posing code already exists in TypeScript. Fifteen hundred
 * clips, one pose each; it costs a few milliseconds.
 *
 * Everything after the body-part line in `AddExtraTouches` is dead: the function returns
 * before it, on a line the author marked as a debug switch and left in. So there is exactly
 * one contact per clip, and this is all that function actually does.
 */

import { Quaternion, Vector3 } from 'three';
import { samplePosition, sampleRotation, type AnimClip } from './clip';
import { Skeleton } from '../skeleton';

/**
 * Every body part in the player model, and the joint it hangs off.
 *
 * From media/objects/players/player.object: each `<geometry>` sits at its parent node's
 * origin with no offset of its own, so a part's position is simply its joint's position.
 */
const BODY_PARTS: readonly (readonly [joint: string, part: string])[] = [
  ['body', 'pelvis'],
  ['middle', 'trunk'],
  ['neck', 'head'],
  ['left_shoulder', 'left_upperarm'],
  ['left_elbow', 'left_lowerarm'],
  ['right_shoulder', 'right_upperarm'],
  ['right_elbow', 'right_lowerarm'],
  ['left_thigh', 'left_upperleg'],
  ['left_knee', 'left_lowerleg'],
  ['left_ankle', 'left_foot'],
  ['right_thigh', 'right_upperleg'],
  ['right_knee', 'right_lowerleg'],
  ['right_ankle', 'right_foot'],
];

const _position = new Vector3();
const _rotation = new Quaternion();

/**
 * Pose a skeleton at one exact frame of a clip, with no blending and no world placement.
 *
 * This is `Animation::Apply(nodeMap, frame, 0, false)`: a time offset of zero makes the
 * midpoint blend collapse onto the frame itself, and smoothing is off, so what comes out is
 * the clip's own pose in the clip's own coordinates — which is the frame the authored ball
 * position was measured against.
 */
function poseAtFrame(skeleton: Skeleton, clip: AnimClip, frame: number): void {
  samplePosition(clip.root, clip.frameCount, frame, _position);
  skeleton.setRootPosition(_position);
  for (const track of clip.joints) {
    const index = skeleton.names.indexOf(track.name);
    if (index < 0) continue;
    sampleRotation(track, clip.frameCount, frame, _rotation);
    skeleton.localRotation[index]!.copy(_rotation);
  }
  skeleton.update();
}

/**
 * Fill in `touchBodyPart` for every clip that touches the ball.
 *
 * Mutates the library in place and returns how many clips were annotated. Clips with no
 * contact are left with the empty string, which is what the original's variable lookup
 * returns for them too.
 */
export function annotateTouchBodyParts(library: AnimClip[], skeleton: Skeleton): number {
  const joints = BODY_PARTS.map(([joint, part]) => ({
    index: skeleton.names.indexOf(joint),
    part,
  })).filter((entry) => entry.index >= 0);

  if (joints.length === 0) {
    throw new Error('The player skeleton has none of the expected body-part joints');
  }

  let annotated = 0;
  for (const clip of library) {
    const touch = clip.touches[0];
    if (!touch) continue;

    poseAtFrame(skeleton, clip, touch.frame);

    let closest = joints[0]!;
    let closestDistance = Infinity;
    for (const entry of joints) {
      const distance = skeleton.worldPosition[entry.index]!.distanceTo(touch.position);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = entry;
      }
    }
    clip.touchBodyPart = closest.part;
    annotated++;
  }

  return annotated;
}

/**
 * `GetTouchTypeForBodyPart` (humanoid_utils.cpp:32-38).
 *
 * A foot or a lower leg means a kick; anything else — chest, head, thigh, shoulder — does
 * not. The distinction exists so a goalkeeper cannot pick up a deliberate back-pass.
 */
export function isKickedBodyPart(bodyPart: string): boolean {
  return bodyPart.includes('foot') || bodyPart.includes('lowerleg');
}
